import { Fragment, type ReactNode } from "react";

// The audit table. Every cell carries its column label in data-l so the phone
// restack can print it. No zebra, no hover, no sort. Empty and loading states
// are full sentences.

export type Column<T> = {
  key: string;
  label: string;
  num?: boolean;
  width?: number | string;
  cell: (row: T) => ReactNode;
};

type Props<T> = {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  loadingText: string;
  empty: string;
  /** Optional full-width row rendered under a row (a thread, a confirm strip). */
  after?: (row: T) => ReactNode;
  footer?: ReactNode;
};

export function DataTable<T>({ columns, rows, rowKey, loading, loadingText, empty, after, footer }: Props<T>) {
  if (loading) return <p className="empty">{loadingText}</p>;
  if (rows.length === 0) return <p className="empty">{empty}</p>;
  return (
    <>
      <table>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.num ? "num" : undefined} style={c.width ? { width: c.width } : undefined}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const sub = after?.(row);
            return (
              <Fragment key={rowKey(row)}>
                <tr>
                  {columns.map((c) => (
                    <td key={c.key} data-l={c.label} className={c.num ? "num" : undefined}>
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
                {sub ? (
                  <tr className="sub">
                    <td colSpan={columns.length}>{sub}</td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {footer ? <p className="empty">{footer}</p> : null}
    </>
  );
}
